import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { toast } from "sonner";
import api from "@/lib/api";
import { 
  KeyRound, Mail, ShieldQuestion, CheckCircle, 
  Loader2, ArrowRight, AlertCircle, Eye, EyeOff 
} from "lucide-react";

export default function ForgotPasswordDialog({ open, onOpenChange, userType = "user" }) {
  const [step, setStep] = useState(1); // 1: email, 2: security question, 3: new password, 4: success
  const [isLoading, setIsLoading] = useState(false);
  const [email, setEmail] = useState("");
  const [securityQuestion, setSecurityQuestion] = useState("");
  const [securityAnswer, setSecurityAnswer] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");

  const resetForm = () => {
    setStep(1);
    setEmail("");
    setSecurityQuestion("");
    setSecurityAnswer("");
    setNewPassword("");
    setConfirmPassword("");
    setError("");
  };

  const handleClose = () => {
    resetForm();
    onOpenChange(false);
  };

  const handleRequestReset = async (e) => {
    e.preventDefault();
    setError("");
    setIsLoading(true);

    try {
      const response = await api.post("/auth/password-reset/request", { email });
      
      if (response.data.has_security_question) {
        setSecurityQuestion(response.data.security_question);
        setStep(2);
      } else {
        // No security question - show email-based reset option
        setError("");
        setStep(5); // New step for email-based reset
      }
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to process request. Please verify your email address.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleVerifyAndReset = async (e) => {
    e.preventDefault();
    setError("");

    // Validate passwords
    if (newPassword !== confirmPassword) {
      setError("Passwords do not match");
      return;
    }

    if (newPassword.length < 8) {
      setError("Password must be at least 8 characters long");
      return;
    }

    setIsLoading(true);

    try {
      await api.post("/auth/password-reset/verify", {
        email,
        security_answer: securityAnswer,
        new_password: newPassword
      });
      
      setStep(4); // Success
      toast.success("Password reset successfully!");
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to reset password. Please check your security answer.");
    } finally {
      setIsLoading(false);
    }
  };

  const getStepTitle = () => {
    switch (step) {
      case 1: return "Reset Your Password";
      case 2: return "Security Verification";
      case 3: return "Create New Password";
      case 4: return "Password Reset Complete";
      case 5: return "Reset via Email";
      default: return "Reset Password";
    }
  };

  const getStepDescription = () => {
    switch (step) {
      case 1: return "Enter your email address to begin the password reset process.";
      case 2: return "Answer your security question to verify your identity.";
      case 3: return "Enter your new password below.";
      case 4: return "Your password has been successfully reset.";
      case 5: return "We'll send a password reset link to your email.";
      default: return "";
    }
  };

  const handleSendResetEmail = async () => {
    setIsLoading(true);
    try {
      await api.post("/auth/password-reset/send-email", { email });
      setStep(6); // Email sent confirmation
      toast.success("Reset email sent!");
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to send reset email");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {step === 4 ? (
              <CheckCircle className="w-5 h-5 text-green-600" />
            ) : (
              <KeyRound className="w-5 h-5 text-primary" />
            )}
            {getStepTitle()}
          </DialogTitle>
          <DialogDescription>{getStepDescription()}</DialogDescription>
        </DialogHeader>

        {/* Progress Indicator */}
        {step < 4 && (
          <div className="flex items-center justify-center gap-2 py-2">
            {[1, 2, 3].map((s) => (
              <div 
                key={s}
                className={`w-8 h-1 rounded-full transition-colors ${
                  s <= step ? "bg-primary" : "bg-muted"
                }`}
              />
            ))}
          </div>
        )}

        {error && (
          <Alert variant="destructive" className="mt-2">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {/* Step 1: Email Input */}
        {step === 1 && (
          <form onSubmit={handleRequestReset} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="reset-email">Email Address</Label>
              <div className="relative">
                <Mail className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                <Input
                  id="reset-email"
                  type="email"
                  placeholder="your@email.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="pl-10"
                  required
                  data-testid="forgot-password-email"
                />
              </div>
            </div>
            
            <DialogFooter className="flex-col sm:flex-row gap-2">
              <Button type="button" variant="outline" onClick={handleClose} className="w-full sm:w-auto">
                Cancel
              </Button>
              <Button type="submit" disabled={isLoading || !email} className="w-full sm:w-auto" data-testid="forgot-password-submit">
                {isLoading ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Checking...</>
                ) : (
                  <>Continue <ArrowRight className="w-4 h-4 ml-2" /></>
                )}
              </Button>
            </DialogFooter>
          </form>
        )}

        {/* Step 2: Security Question */}
        {step === 2 && (
          <form onSubmit={(e) => { e.preventDefault(); setStep(3); }} className="space-y-4">
            <div className="space-y-2">
              <Label>Security Question</Label>
              <div className="p-3 bg-muted rounded-lg flex items-start gap-2">
                <ShieldQuestion className="w-4 h-4 mt-0.5 text-primary flex-shrink-0" />
                <p className="text-sm font-medium">{securityQuestion}</p>
              </div>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="security-answer">Your Answer</Label>
              <Input
                id="security-answer"
                type="text"
                placeholder="Enter your answer"
                value={securityAnswer}
                onChange={(e) => setSecurityAnswer(e.target.value)}
                required
                data-testid="forgot-password-answer"
              />
              <p className="text-xs text-muted-foreground">Answer is case-insensitive</p>
            </div>
            
            <DialogFooter className="flex-col sm:flex-row gap-2">
              <Button type="button" variant="outline" onClick={() => setStep(1)} className="w-full sm:w-auto">
                Back
              </Button>
              <Button type="submit" disabled={!securityAnswer} className="w-full sm:w-auto" data-testid="forgot-password-verify">
                Continue <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </DialogFooter>
          </form>
        )}

        {/* Step 3: New Password */}
        {step === 3 && (
          <form onSubmit={handleVerifyAndReset} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="new-password">New Password</Label>
              <div className="relative">
                <Input
                  id="new-password"
                  type={showPassword ? "text" : "password"}
                  placeholder="Enter new password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  required
                  minLength={8}
                  data-testid="forgot-password-new"
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="absolute right-1 top-1 h-8 w-8"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </Button>
              </div>
              <p className="text-xs text-muted-foreground">Minimum 8 characters</p>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="confirm-password">Confirm Password</Label>
              <Input
                id="confirm-password"
                type={showPassword ? "text" : "password"}
                placeholder="Confirm new password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
                data-testid="forgot-password-confirm"
              />
            </div>
            
            <DialogFooter className="flex-col sm:flex-row gap-2">
              <Button type="button" variant="outline" onClick={() => setStep(2)} className="w-full sm:w-auto">
                Back
              </Button>
              <Button 
                type="submit" 
                disabled={isLoading || !newPassword || !confirmPassword} 
                className="w-full sm:w-auto"
                data-testid="forgot-password-reset"
              >
                {isLoading ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Resetting...</>
                ) : (
                  <>Reset Password <KeyRound className="w-4 h-4 ml-2" /></>
                )}
              </Button>
            </DialogFooter>
          </form>
        )}

        {/* Step 4: Success */}
        {step === 4 && (
          <div className="text-center py-4">
            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-green-100 flex items-center justify-center">
              <CheckCircle className="w-8 h-8 text-green-600" />
            </div>
            <p className="text-lg font-medium mb-2">Password Reset Successful!</p>
            <p className="text-sm text-muted-foreground mb-6">
              You can now sign in with your new password.
            </p>
            <Button onClick={handleClose} className="w-full" data-testid="forgot-password-done">
              Return to Sign In
            </Button>
          </div>
        )}

        {/* Step 5: Email-based reset option */}
        {step === 5 && (
          <div className="space-y-4">
            <Alert className="bg-blue-50 border-blue-200">
              <Mail className="h-4 w-4 text-blue-600" />
              <AlertDescription className="text-blue-800">
                No security question is set for this account. We can send a password reset link to your email instead.
              </AlertDescription>
            </Alert>
            
            <div className="p-4 bg-muted rounded-lg">
              <p className="text-sm font-medium mb-1">Reset link will be sent to:</p>
              <p className="text-primary">{email}</p>
            </div>
            
            <DialogFooter className="flex-col sm:flex-row gap-2">
              <Button type="button" variant="outline" onClick={() => setStep(1)} className="w-full sm:w-auto">
                Back
              </Button>
              <Button onClick={handleSendResetEmail} disabled={isLoading} className="w-full sm:w-auto">
                {isLoading ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Sending...</>
                ) : (
                  <><Mail className="w-4 h-4 mr-2" /> Send Reset Email</>
                )}
              </Button>
            </DialogFooter>
          </div>
        )}

        {/* Step 6: Email sent confirmation */}
        {step === 6 && (
          <div className="text-center py-4">
            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-blue-100 flex items-center justify-center">
              <Mail className="w-8 h-8 text-blue-600" />
            </div>
            <p className="text-lg font-medium mb-2">Check Your Email</p>
            <p className="text-sm text-muted-foreground mb-4">
              We've sent a password reset link to <strong>{email}</strong>
            </p>
            <p className="text-xs text-muted-foreground mb-6">
              The link will expire in 1 hour. Check your spam folder if you don't see it.
            </p>
            <Button onClick={handleClose} className="w-full" data-testid="forgot-password-email-done">
              Got It
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
